// src/components/projects/ViewSelector.jsx
import React from 'react';
import {
  Box,
  Tabs,
  Tab,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  ViewList as ListIcon,
  ViewKanban as BoardIcon,
  CalendarToday as CalendarIcon,
  Timeline as TimelineIcon,
  Group as TeamIcon,
  InsertDriveFile as DocumentsIcon,
  MonetizationOn as BudgetIcon,
  MoreHoriz as MoreIcon,
  ArrowDropDown as DropdownIcon,
  GridView as GridIcon,
  BoltOutlined as AutomationIcon
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import useAuthStore from '../../stores/authStore';
import { PERMISSION_RESOURCES, PERMISSION_ACTIONS } from '../../constants/permissionConstants';

/**
 * ViewSelector component for switching between project views
 * Updated with Aghrba theme design and magical UI elements
 * 
 * @param {Object} props - Component props 
 * @param {string} props.projectId - Project ID for navigation
 */
const ViewSelector = ({ projectId }) => {
  const theme = useTheme();
  const location = useLocation();
  const { hasPermission } = useAuthStore();
  const isMobile = useMediaQuery(theme.breakpoints?.down('md') || '(max-width: 960px)');
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = React.useState(null);
  const [viewMenuAnchorEl, setViewMenuAnchorEl] = React.useState(null);
  
  // Primary views always shown in tabs
  const primaryViews = [
    { id: 'overview', label: 'Overview', icon: <DashboardIcon sx={{ fontSize: 20 }} />, path: `/projects/${projectId}` },
    { id: 'list', label: 'List', icon: <ListIcon sx={{ fontSize: 20 }} />, path: `/projects/${projectId}/tasks` },
    { id: 'board', label: 'Board', icon: <BoardIcon sx={{ fontSize: 20 }} />, path: `/projects/${projectId}/kanban` },
    { id: 'grid', label: 'Grid', icon: <GridIcon sx={{ fontSize: 20 }} />, path: `/projects/${projectId}/grid` },
    { id: 'calendar', label: 'Calendar', icon: <CalendarIcon sx={{ fontSize: 20 }} />, path: `/projects/${projectId}/calendar` },
    { id: 'documents', label: 'Documents', icon: <DocumentsIcon sx={{ fontSize: 20 }} />, path: `/projects/${projectId}/documents` },

    // { id: 'timeline', label: 'Timeline', icon: <TimelineIcon sx={{ fontSize: 20 }} />, path: `/projects/${projectId}/timeline` }
  ];
  
  // Secondary views shown in the "More" dropdown on desktop
  const allSecondaryViews = [
    { id: 'team', label: 'Team', icon: <TeamIcon sx={{ fontSize: 20 }} />, path: `/projects/${projectId}/team` },
    { id: 'budget', label: 'Budget', icon: <BudgetIcon sx={{ fontSize: 20 }} />, path: `/projects/${projectId}/budget` },
    { 
      id: 'automation', 
      label: 'Automation', 
      icon: <AutomationIcon sx={{ fontSize: 20 }} />, 
      path: `/projects/${projectId}/automation`,
      requiresPermission: { resource: PERMISSION_RESOURCES.WORKFLOW_AUTOMATION, action: PERMISSION_ACTIONS.READ }
    }
  ];
  
  // Filter secondary views based on permissions
  const secondaryViews = allSecondaryViews.filter(view => {
    if (view.requiresPermission) {
      return hasPermission(view.requiresPermission.resource, view.requiresPermission.action);
    }
    return true;
  });
  
  // All views combined for mobile view
  const allViews = [...primaryViews, ...secondaryViews];
  
  // Handle menu operations
  const handleOpenMoreMenu = (event) => {
    setMoreMenuAnchorEl(event.currentTarget);
  };
  
  const handleCloseMoreMenu = () => {
    setMoreMenuAnchorEl(null);
  };
  
  const handleOpenViewMenu = (event) => {
    setViewMenuAnchorEl(event.currentTarget);
  };
  
  const handleCloseViewMenu = () => {
    setViewMenuAnchorEl(null);
  };
  
  // Get current view from path
  const getViewFromPath = (path) => {
    // Check if we're at the project root (overview)
    const projectRootPattern = new RegExp(`^/projects/${projectId}/?$`);
    if (projectRootPattern.test(path)) return 'overview';
    
    if (path.includes('/tasks')) return 'list';
    if (path.includes('/kanban')) return 'board';
    if (path.includes('/grid')) return 'grid';
    if (path.includes('/calendar')) return 'calendar';
    if (path.includes('/timeline')) return 'timeline';
    if (path.includes('/team')) return 'team';
    if (path.includes('/documents')) return 'documents';
    if (path.includes('/budget')) return 'budget';
    if (path.includes('/automation')) return 'automation';
    return 'overview';
  };
  
  // Determine the current view
  const currentView = getViewFromPath(location.pathname);
  
  // Get current view data
  const getCurrentViewData = () => {
    return allViews.find(view => view.id === currentView) || primaryViews[0];
  };
  
  // Check if theme has Aghrba extensions
  const hasAghrbaTheme = theme?.aghrba?.tokens;
  
  // Get theme values safely
  const getThemeValue = (path, fallback) => {
    try {
      const value = path.split('.').reduce((o, p) => o?.[p], theme);
      return value || fallback;
    } catch {
      return fallback;
    }
  };
  
  // Mobile dropdown view
  if (isMobile) {
    const currentViewData = getCurrentViewData();
    
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center',
          py: 1,
          px: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Box
          onClick={handleOpenViewMenu}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            py: 0.5,
            px: 1,
            // borderRadius: getThemeValue('aghrba.tokens.borderRadius.small', 1),
            backgroundColor: `${theme.palette.secondary.main}08`,
            transition: getThemeValue('aghrba.tokens.transitions.standard', 'all 0.3s ease'),
            '&:hover': {
              backgroundColor: `${theme.palette.secondary.main}10`,
            }
          }}
        >
          {React.cloneElement(currentViewData.icon, {
            sx: { 
              color: theme.palette.secondary.main,
              fontSize: 20
            }
          })}
          <Box 
            component="span" 
            sx={{ 
              ml: 1, 
              mr: 0.5,
              fontFamily: getThemeValue('aghrba.typography.heading.secondary', 'inherit'),
              fontWeight: 600,
              color: theme.palette.text.primary
            }}
          >
            {currentViewData.label}
          </Box>
          <DropdownIcon sx={{ fontSize: 20 }} />
        </Box>
        
        <Menu
          anchorEl={viewMenuAnchorEl}
          open={Boolean(viewMenuAnchorEl)}
          onClose={handleCloseViewMenu}
          PaperProps={{
            elevation: 3,
            sx: { 
              width: 220,
              maxHeight: '70vh',
              mt: 1,
              // borderRadius: getThemeValue('aghrba.tokens.borderRadius.small', 1),
              backgroundColor: theme.palette.background.paper,
            }
          }}
        >
          {allViews.map((view) => (
            <MenuItem
              key={view.id}
              component={Link}
              to={view.path}
              onClick={handleCloseViewMenu}
              selected={currentView === view.id}
              sx={{
                // borderRadius: 0.5,
                mx: 0.5,
                my: 0.25,
                '&.Mui-selected': {
                  backgroundColor: `${theme.palette.secondary.main}10`,
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {view.icon}
              </ListItemIcon>
              <ListItemText primary={view.label} />
            </MenuItem>
          ))}
        </Menu>
      </Box>
    );
  }
  
  // Desktop view with tabs and more dropdown
  return (
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'center',
        // borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Tabs
        value={primaryViews.findIndex(view => view.id === currentView) !== -1
          ? currentView
          : false}
        aria-label="project views"
        sx={{
          minHeight: 48,
          '& .MuiTabs-indicator': {
            backgroundColor: theme.palette.secondary.main,
            height: 3,
            borderRadius: '2px 2px 0 0',
            boxShadow: `0 0 6px ${theme.palette.secondary.main}`,
          }
        }}
      >
        {primaryViews.map((view) => (
          <Tab
            key={view.id}
            icon={view.icon}
            iconPosition="start"
            label={view.label}
            value={view.id}
            component={Link}
            to={view.path}
            sx={{
              color: theme.palette.mode === 'dark'
                ? theme.palette.common.white
                : theme.palette.common.black,
              '&.Mui-selected': {
                color: theme.palette.secondary.main,
              }
            }}
          />
        ))}
      </Tabs>
      
      {/* More views dropdown */}
      <Box sx={{ ml: 'auto', mr: 2 }}>
        <Tooltip 
          title="More views"
          placement="bottom"
        >
          <IconButton
            onClick={handleOpenMoreMenu}
            color={secondaryViews.some(view => view.id === currentView) ? 'primary' : 'default'}
            sx={{
              '&:hover': {
                backgroundColor: `${theme.palette.secondary.main}08`,
              }
            }}
          >
            <MoreIcon />
          </IconButton>
        </Tooltip>
        
        <Menu
          anchorEl={moreMenuAnchorEl}
          open={Boolean(moreMenuAnchorEl)}
          onClose={handleCloseMoreMenu}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            elevation: 3,
            sx: { 
              width: 200,
              mt: 1,
              // borderRadius: getThemeValue('aghrba.tokens.borderRadius.small', 1),
              backgroundColor: theme.palette.background.paper,
            }
          }}
        >
          {secondaryViews.map((view) => (
            <MenuItem
              key={view.id}
              component={Link}
              to={view.path}
              onClick={handleCloseMoreMenu}
              selected={currentView === view.id}
              sx={{
                // borderRadius: 0.5,
                mx: 0.5,
                my: 0.25,
                '&.Mui-selected': {
                  backgroundColor: `${theme.palette.secondary.main}10`,
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {view.icon}
              </ListItemIcon>
              <ListItemText primary={view.label} />
            </MenuItem>
          ))}
        </Menu>
      </Box>
    </Box>
  );
};

export default ViewSelector;